import {useRequestPermissionUpgradeMutation} from "@/api/companyApi";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import {useSelectedCompanyId, useUserPermission} from "@/hooks/useUserHooks";
import ImagePlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import {isRequestPermissionsModalOpenAtom} from "@/store/jotaiAtoms";
import type {TPermission} from "@/store/types";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const RequestPermissionsModal: FC = () => {
  const {t} = useTranslation("multi_account");
  const user = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const selectedCompanyId = useSelectedCompanyId();
  const setIsRequestPermissionsModalVisible = useSetAtom(isRequestPermissionsModalOpenAtom);
  const excludedPermissions = useMemo((): TPermission[] => {
    if (userPermission === "READ") return ["READ"];
    if (userPermission === "PURCHASE") return ["READ", "PURCHASE"];
    return [];
  }, [userPermission]);
  const [permissionToRequest, setPermissionToRequest] = useState<TPermission | null>(null);

  const {mutate: requestPermissionUpgrade, isPending: isRequestingPermissionUpgrade} =
    useRequestPermissionUpgradeMutation({
      companyId: selectedCompanyId!,
      onSuccessCallback: () => setIsRequestPermissionsModalVisible(false),
    });

  return (
    <Modal
      onClose={() => setIsRequestPermissionsModalVisible(false)}
      className="min-h-72"
      backgroundClassName="max-w-[900px]"
      modalTitle={t("permission_request")}
      customBottomRow={
        <div className="border-neutral-white-dark flex shrink-0 grow gap-3 border-t-1 p-5">
          <Button
            isDisabled={!permissionToRequest || isRequestingPermissionUpgrade}
            isLoading={isRequestingPermissionUpgrade}
            onClick={() => {
              requestPermissionUpgrade({
                newPermission: permissionToRequest!,
              });
            }}
            buttonStyle="Gradient"
            className="h-auto px-8 py-4 sm:min-w-[360px]">
            <span className="z-10 font-medium md:text-lg">{t("request")}</span>
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-5">
        <div className="border-neutral-grey-light-dark flex items-center justify-between gap-3 rounded-full border p-2">
          <div className="flex items-center gap-3">
            <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
              <img src={user.profile_picture ?? ImagePlaceholder} className="h-full w-full rounded-full object-fill" />
            </div>
            <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
              {user.first_name}
            </span>
          </div>
          <span className="text-neutral-black text-right text-xs">
            {t(`${userPermission.toLowerCase()}_permissions`)}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-neutral-black text-xs">{t("permission_needed")}</span>
          <PermissionSelect
            value={permissionToRequest}
            onChange={value => setPermissionToRequest(value as TPermission)}
            placeholder={t("permission_select_placeholder")}
            excludedPermissions={excludedPermissions}
            containerClassName="md:max-w-[auto] [&_button]:h-auto [&_button]:min-h-[60px] [&_button]:px-3 [&_button]:py-2 [&_button>span]:text-base"
            dropdownClassName="translate-y-16"
            dropDownIconVariant="FILLED"
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestPermissionsModal;
